'use client';

import React, { useEffect, useState } from 'react';
import { LargePrimaryButton } from '../../../components/core/buttons';
import { RegularBody, SemiboldBody, SemiboldHeader2 } from '../../../components/core/typography';
import FormInput from '../../../components/formBuilder/formBuilder';
import { Form, message } from 'antd';
import { getIcon } from '../../../components/core/vticon';
import { useNavigate } from 'react-router-dom';
import { createPassword, updatePassword } from '../../../services/api';

const PasswordScreen = () => {
	const [form] = Form.useForm();
	const navigate = useNavigate();
	
	// Get params from URL
	const searchParams = typeof window !== 'undefined' ? 
		new URLSearchParams(window.location.search) : null;

	const email = searchParams?.get('id');
	const isForgot = searchParams?.get('forgot');
	const [loading, setLoading] = useState(false);
	const [strongPasswordConditions, setStrongPasswordConditions] = useState([
		{ label: 'Must be at least 8 characters', istrue: false },
		{ label: 'Must contain one special character', istrue: false },
		{ label: 'Must contain one number', istrue: false },
		{ label: 'Must contain one upper case', istrue: false },
		{ label: 'Must contain one lower case', istrue: false },
	]);
	const [passwordsMatch, setPasswordsMatch] = useState(false);

	const handleStatusUpdate = (value: any) => {
		let password = value.password;

		const updatedConditions = [
			{ label: 'Must be at least 8 characters', istrue: password.length >= 8 },
			{ label: 'Must contain one special character', istrue: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
			{ label: 'Must contain one number', istrue: /\d/.test(password) },
			{ label: 'Must contain one upper case', istrue: /[A-Z]/.test(password) },
			{ label: 'Must contain one lower case', istrue: /[a-z]/.test(password) },
		];
		setStrongPasswordConditions(updatedConditions);

		const confirmPassword = value.confirm_password;
		setPasswordsMatch(password && confirmPassword && password === confirmPassword);
	};

	const renderIcon = (isvalid: boolean) => {
		return (
			<div
				className={`h-[20px] w-[20px] flex justify-center items-center ${
					isvalid ? 'bg-[#DCFAE6]' : 'bg-[#D0D5DD]'
				} rounded-full`}
			>
				{getIcon('CheckIcon', 8, 8, isvalid ? '#17B26A' : '#FFFFFF')}
			</div>
		);
	};

	const renderPasswordCheck = () => {
		return (
			<div>
				{strongPasswordConditions.map((i) => (
					<div key={i.label} className='mb-[12px] flex gap-2'>
						{renderIcon(i.istrue)}
						<RegularBody text={i.label} color='#667085' />
					</div>
				))}
			</div>
		);
	};

	const validateConfirmPassword = (_: any, value: string) => {
		if (value && value !== form.getFieldValue('password')) {
			return Promise.reject(new Error('Passwords do not match!'));
		} else {
			return Promise.resolve();
		}
	};

	const handleSubmit = async () => {
		try {
			setLoading(true);
			await form.validateFields();
			const values = form.getFieldsValue();
			let response = isForgot=='false'?await createPassword({ email, newPassword: values.password }):
			await updatePassword({ email, newPassword: values.password });

			if (response.success) {
				message.success(response.message);
				navigate('/signin-new');
			} else {
				message.error(response.message);
			}
		} catch (errorInfo) {
			console.error('Validation Failed:', errorInfo);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className='flex items-center justify-center bg-[#E0E5EB] min-h-screen py-[2%]'>
		<div className='bg-[#FFFFFF] px-[5%] py-[2%] flex flex-col justify-center w-full max-w-[600px] min-h-[90vh]'>
			<div className='flex items-center w-full justify-center'>
				<div className='bg-[#F4EBFF] rounded-full h-[56px] w-[56px] flex items-center justify-center'>
					{getIcon('Lock01', 28, 28, '#7F56D9')}
				</div>
			</div>
			<div className='mt-[24px] mb-[32px] flex w-full items-center justify-center flex-col'>
				<SemiboldHeader2 text={isForgot == 'true' ? 'Reset your password' : 'Create your password'} color={'#101828'} />
				<RegularBody text={'This password will be used to login to your account'} color='#667085' />
			</div>
	
			<Form
				layout='vertical'
				initialValues={{ remember: true }}
				form={form}
				onChange={() => handleStatusUpdate(form.getFieldsValue())}
			>
				<div>
					<FormInput
						name='password'
						type='password'
						label='Password'
						width='100%'
						placeholder='Enter here'
						rules={[
							{
								required: true,
								message: 'Please enter your password',
							},
						]}
					/>
					<FormInput
						name='confirm_password'
						type='password'
						label='Confirm password'
						width='100%'
						placeholder='Enter here'
						onPaste={(e:any) => e.preventDefault()}
						rules={[
							{
								required: true,
								message: 'Please confirm your password',
							},
							{ validator: validateConfirmPassword },
						]}
					/>
				</div>
	
				<div>{renderPasswordCheck()}</div>
				<div className='w-full mt-[24px]'>
					<LargePrimaryButton
						text={'Create Password'}
						loading={loading}	
						handleClick={handleSubmit}
						style={{ width: '100%' }}
						disabled={
							strongPasswordConditions.filter((i) => i.istrue !== true).length > 0 || !passwordsMatch
						}
					/>
				</div>
	
				<div className='mt-[32px] cursor-pointer w-full flex items-center justify-center gap-2' onClick={() => navigate('/signin-new')}>
					{getIcon('ArrowLeft', 20, 20, '#475467')}
					<SemiboldBody text={'Back to log in'} color='#475467' />
				</div>
			</Form>
		</div>
	</div>
	
	

	);
};

export default PasswordScreen;
